import React, { useState, useEffect } from "react";
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Box,
  Card,
  CardContent,
  Alert,
  Snackbar,
  IconButton,
  Tooltip,
  Divider,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Avatar,
  Badge,
  Tabs,
  Tab,
  alpha,
  InputAdornment,
  useTheme,
  Breadcrumbs,
  CircularProgress,
  Backdrop,
  Fade,
  tooltipClasses,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import Grid from "@mui/material/Unstable_Grid2";
import {
  ContentCopy,
  Download,
  Save,
  AutoAwesome,
  Refresh,
  Business,
  Campaign,
  Lightbulb,
  Person,
  Close,
  Delete,
  Visibility,
  Search,
  FilterList,
  Star,
  ChevronRight,
  StarBorder,
  ChatBubbleOutline,
  TrendingUp,
  Group,
  RocketLaunch,
  Psychology,
  Palette,
  Insights,
  AttachMoney,
  LocalOffer,
  Fullscreen,
  FullscreenExit,
  FormatBold,
  FormatItalic,
  FormatListBulleted,
  FormatListNumbered,
  Link,
  Image,
  Home,
  Article,
  AutoFixHigh,
  Share,
} from "@mui/icons-material";
import { Link as RouterLink, useHistory } from "react-router-dom";
import newBlogService from "../services/new_blogsServices";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";


const ModernTooltip = styled(({ className, ...props }) => (
  <Tooltip
    {...props}
    arrow
    classes={{ popper: className }}
    PopperProps={{
      modifiers: [
        {
          name: "zIndex",
          enabled: true,
          phase: "write",
          fn: ({ state }) => {
            state.styles.popper.zIndex = 99999;
          },
        },
      ],
    }}
  />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor:
      theme.palette.mode === "light"
        ? theme.palette.common.white
        : theme.palette.grey[900],
    color:
      theme.palette.mode === "light"
        ? theme.palette.primary.main
        : theme.palette.common.white,
    boxShadow: theme.shadows[6],
    fontSize: 12,
    fontWeight: 500,
    borderRadius: 10,
    padding: "8px 14px",
    backdropFilter: "blur(6px)",
  },
  [`& .${tooltipClasses.arrow}`]: {
    color:
      theme.palette.mode === "light"
        ? theme.palette.common.white
        : theme.palette.grey[900],
  },
}));

const tools = [
  {
    id: "slogan",
    name: "Slogan Generator",
    icon: <LocalOffer />,
    color: "#6366f1",
    gradient: "linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)",
    questions: [
      "Tell us about your brand or business",
      "What are your business's unique selling products/features?",
      "Who is your target audience or user base?",
    ],
    placeholder: "Describe your business details...",
    description: "Create catchy and memorable slogans for your brand",
  },
  {
    id: "strategy",
    name: "Brand Strategy",
    icon: <Insights />,
    color: "#10b981",
    gradient: "linear-gradient(135deg, #10b981 0%, #34d399 100%)",
    questions: [
      "Tell us about your brand or business",
      "What is your brand's or company's mission?",
    ],
    placeholder: "Describe your brand vision...",
    description: "Develop comprehensive brand strategies and positioning",
  },
  {
    id: "idea",
    name: "Business Idea Generator",
    icon: <RocketLaunch />,
    color: "#f59e0b",
    gradient: "linear-gradient(135deg, #f59e0b 0%, #fbbf24 100%)",
    questions: [
      "What industries or areas are you interested in?",
      "What is your available budget range?",
    ],
    placeholder: "Share your interests and budget...",
    description: "Generate innovative business ideas based on your interests",
  },
  {
    id: "persona",
    name: "Persona Generator",
    icon: <Psychology />,
    color: "#8b5cf6",
    gradient: "linear-gradient(135deg, #8b5cf6 0%, #a78bfa 100%)",
    questions: ["What is your company name?", "Tell us about your company"],
    placeholder: "Describe your company...",
    description: "Create detailed customer personas for your business",
  },
];

// Function to format generated content with titles and emojis
const formatGeneratedContent = (content, toolId) => {
  if (!content) return "";

  const emojiMap = {
    slogan: ["🚀", "💡", "✨", "🌟", "🔥", "🎯", "💎", "⚡"],
    strategy: ["📊", "🎯", "🚀", "💼", "🏆", "⭐", "📈", "🎨"],
    idea: ["💡", "🚀", "🌟", "💰", "🏢", "🛠️", "🎯", "✨"],
    persona: ["👤", "🎯", "💼", "👥", "📊", "🤝", "💡", "🌟"],
  };

  const toolEmojis = emojiMap[toolId] || ["✨", "💡", "🌟"];

  // Helper function to get a random emoji
  const getRandomEmoji = () => {
    return toolEmojis[Math.floor(Math.random() * toolEmojis.length)];
  };

  // Format based on tool type
  switch (toolId) {
    case "slogan":
      return content
        .split("\n")
        .map((line, index) => {
          if (line.trim() && !line.includes("**") && line.length > 10) {
            return `${getRandomEmoji()} **${line.trim()}**`;
          }
          return line;
        })
        .join("\n");

    case "strategy":
      const sections = content.split("\n\n");
      return sections
        .map((section, index) => {
          const lines = section.split("\n");
          if (lines[0] && lines[0].includes(":")) {
            const [title, ...contentLines] = lines[0].split(":");
            return `${getRandomEmoji()} **${title.trim()}:**${contentLines.join(":")}\n${lines.slice(1).join("\n")}`;
          }
          return section;
        })
        .join("\n\n");

    case "idea":
      return content
        .split("\n")
        .map((line, index) => {
          if (line.trim() && line.includes(":")) {
            const [title, ...content] = line.split(":");
            return `${getRandomEmoji()} **${title.trim()}:** ${content.join(":").trim()}`;
          } else if (line.trim() && line.length > 20) {
            return `${getRandomEmoji()} **${line.trim()}**`;
          }
          return line;
        })
        .join("\n");

    case "persona":
      return content
        .split("\n")
        .map((line, index) => {
          if (line.trim() && line.includes(":")) {
            const [title, ...content] = line.split(":");
            const emoji = title.toLowerCase().includes("name")
              ? "👤"
              : title.toLowerCase().includes("age")
                ? "🎂"
                : title.toLowerCase().includes("occupation")
                  ? "💼"
                  : title.toLowerCase().includes("income")
                    ? "💰"
                    : title.toLowerCase().includes("goals")
                      ? "🎯"
                      : title.toLowerCase().includes("pain")
                        ? "😟"
                        : title.toLowerCase().includes("channels")
                          ? "📱"
                          : title.toLowerCase().includes("motivation")
                            ? "💪"
                            : getRandomEmoji();
            return `${emoji} **${title.trim()}:** ${content.join(":").trim()}`;
          }
          return line;
        })
        .join("\n");

    default:
      return content;
  }
};

// Function to render formatted content with styles
const renderFormattedContent = (content) => {
  if (!content) return null;

  const lines = content.split("\n");
  return lines.map((line, index) => {
    if (line.includes("**")) {
      // Extract bold text
      const parts = line.split("**");
      return (
        <Box key={index} sx={{ mb: 1.5 }}>
          {parts.map((part, partIndex) => {
            if (partIndex % 2 === 1) {
              // This is bold text
              return (
                <Typography
                  key={partIndex}
                  component="span"
                  sx={{
                    fontWeight: "bold",
                    color: "primary.main",
                    fontSize: "1.1rem",
                    mr: 0.5,
                  }}
                >
                  {part}
                </Typography>
              );
            } else {
              // This is regular text
              return (
                <Typography
                  key={partIndex}
                  component="span"
                  sx={{
                    color: "text.primary",
                    fontSize: "1rem",
                  }}
                >
                  {part}
                </Typography>
              );
            }
          })}
        </Box>
      );
    }

    // Regular line
    return (
      <Typography
        key={index}
        sx={{
          mb: 1,
          color: "text.primary",
          fontSize: "1rem",
          lineHeight: 1.6,
        }}
      >
        {line || <br />}
      </Typography>
    );
  });
};

const AIBusinessTools = () => {
  const theme = useTheme();
  const history = useHistory();
  const [activeTool, setActiveTool] = useState(tools[0]);
  const [answers, setAnswers] = useState(
    Array(tools[0].questions.length).fill(""),
  );
  const [generatedContent, setGeneratedContent] = useState("");
  const [formattedContent, setFormattedContent] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadingTool, setLoadingTool] = useState(null);

  const extractContent = (res) => {
    return res?.data?.content || "";
  };

  const [savedContents, setSavedContents] = useState([]);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [fullscreenDialogOpen, setFullscreenDialogOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedTab, setSelectedTab] = useState(0);
  const [starredItems, setStarredItems] = useState(new Set());
  const [showEditorToolbar, setShowEditorToolbar] = useState(false);

  const handleBreadcrumbClick = (event, path) => {
    event.preventDefault();
    window.location.href = "/docTags";
    // history.push(path);
  };

  // Initialize answers based on active tool
  useEffect(() => {
    setAnswers(Array(activeTool.questions.length).fill(""));
  }, [activeTool]);

  // Update formatted content when generated content changes
  useEffect(() => {
    if (generatedContent) {
      const formatted = formatGeneratedContent(generatedContent, activeTool.id);
      setFormattedContent(formatted);
    } else {
      setFormattedContent("");
    }
  }, [generatedContent, activeTool.id]);

  const handleToolClick = (tool) => {
    setActiveTool(tool);
    setGeneratedContent("");
    setFormattedContent("");
    setShowEditorToolbar(false);
  };

  const handleAnswerChange = (index, value) => {
    const newAnswers = [...answers];
    newAnswers[index] = value;
    setAnswers(newAnswers);
  };

  const startLoading = (toolId) => {
    setLoading(true);
    setLoadingTool(toolId);
  };

  const stopLoading = () => {
    setLoading(false);
    setLoadingTool(null);
  };

  const generateSloganFromAPI = async () => {
    if (answers.some((a) => a.trim() === "")) {
      setSnackbar({
        open: true,
        message: "Please answer all questions before generating!",
        severity: "warning",
      });
      return;
    }

    try {
      startLoading("slogan");

      const payload = {
        brand_business: answers[0],
        usp_features: answers[1],
        target_audience: answers[2],
        tone: "friendly",
        language: "English",
      };

      const res = await newBlogService.generateSlogan(payload);

      if (res.status === 200) {
        const content = extractContent(res);
        setGeneratedContent(content);
        setShowEditorToolbar(true);

        setSnackbar({
          open: true,
          message: "✨ Slogans generated successfully!",
          severity: "success",
        });
      }
    } catch (err) {
      console.error(err);
      setSnackbar({
        open: true,
        message: "Failed to generate slogans",
        severity: "error",
      });
    } finally {
      stopLoading();
    }
  };

  const generateBrandStrategyFromAPI = async () => {
    if (answers.some((a) => a.trim() === "")) {
      setSnackbar({
        open: true,
        message: "Please answer all questions before generating!",
        severity: "warning",
      });
      return;
    }

    try {
      startLoading("strategy");

      const payload = {
        brand_business: answers[0],
        mission: answers[1],
        tone: "Professional",
        language: "English",
      };

      const res = await newBlogService.generateBrandStrategy(payload);

      if (res.status === 200) {
        const content = extractContent(res);
        setGeneratedContent(content);
        setShowEditorToolbar(true);

        setSnackbar({
          open: true,
          message: "✨ Brand strategy generated successfully!",
          severity: "success",
        });
      }
    } catch (err) {
      console.error(err);
      setSnackbar({
        open: true,
        message: "Failed to generate brand strategy",
        severity: "error",
      });
    } finally {
      stopLoading();
    }
  };

  const generateBusinessIdeaFromAPI = async () => {
    if (answers.some((a) => a.trim() === "")) {
      setSnackbar({
        open: true,
        message: "Please answer all questions before generating!",
        severity: "warning",
      });
      return;
    }

    try {
      startLoading("idea");

      const payload = {
        industry_interest: answers[0],
        budget_range: answers[1],
        tone: "friendly",
        language: "English",
      };

      const res = await newBlogService.generateBusinessIdea(payload);

      if (res.status === 200) {
        const content = extractContent(res);
        setGeneratedContent(content);
        setShowEditorToolbar(true);

        setSnackbar({
          open: true,
          message: "✨ Business ideas generated successfully!",
          severity: "success",
        });
      }
    } catch (err) {
      console.error(err);
      setSnackbar({
        open: true,
        message: "Failed to generate business ideas",
        severity: "error",
      });
    } finally {
      stopLoading();
    }
  };

  const generatePersonaFromAPI = async () => {
    if (answers.some((a) => a.trim() === "")) {
      setSnackbar({
        open: true,
        message: "Please answer all questions before generating!",
        severity: "warning",
      });
      return;
    }

    try {
      startLoading("persona");

      const payload = {
        company_name: answers[0],
        company_details: answers[1],
        tone: "professional",
        language: "English",
      };

      const res = await newBlogService.generatePersona(payload);

      if (res.status === 200) {
        const content = extractContent(res);
        setGeneratedContent(content);
        setShowEditorToolbar(true);

        setSnackbar({
          open: true,
          message: "✨ Personas generated successfully!",
          severity: "success",
        });
      }
    } catch (err) {
      console.error(err);
      setSnackbar({
        open: true,
        message: "Failed to generate personas",
        severity: "error",
      });
    } finally {
      stopLoading();
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(generatedContent);
    setSnackbar({
      open: true,
      message: "📋 Copied to clipboard!",
      severity: "success",
    });
  };

  const handleCopyFormatted = () => {
    navigator.clipboard.writeText(formattedContent);
    setSnackbar({
      open: true,
      message: "📋 Formatted content copied!",
      severity: "success",
    });
  };

  const handleSave = () => {
    if (!generatedContent) {
      setSnackbar({
        open: true,
        message: "Generate content first!",
        severity: "warning",
      });
      return;
    }

    const newSavedContent = {
      id: Date.now(),
      tool: activeTool.name,
      toolId: activeTool.id,
      content: generatedContent.substring(0, 100) + "...", // Preview only
      fullContent: generatedContent,
      formattedContent: formattedContent,
      date: new Date().toISOString(),
      color: activeTool.color,
      icon: activeTool.icon,
      starred: false,
    };

    setSavedContents([newSavedContent, ...savedContents]);
    setSaveDialogOpen(true);
    setSnackbar({
      open: true,
      message: "💾 Content saved successfully!",
      severity: "success",
    });
  };

  const handleDownload = () => {
    if (!generatedContent) return;

    const element = document.createElement("a");
    const file = new Blob([generatedContent], { type: "text/plain" });
    element.href = URL.createObjectURL(file);
    element.download = `${activeTool.name.toLowerCase().replace(/\s+/g, "_")}_${Date.now()}.txt`;
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);

    setSnackbar({
      open: true,
      message: "📥 Download started!",
      severity: "success",
    });
  };

  const handleClear = () => {
    setAnswers(Array(activeTool.questions.length).fill(""));
    setGeneratedContent("");
    setFormattedContent("");
    setShowEditorToolbar(false);
  };

  const handleStarToggle = (id) => {
    const newStarred = new Set(starredItems);
    if (newStarred.has(id)) {
      newStarred.delete(id);
    } else {
      newStarred.add(id);
    }
    setStarredItems(newStarred);
  };

  const handleDeleteSaved = (id) => {
    setSavedContents(savedContents.filter((item) => item.id !== id));
    setSnackbar({
      open: true,
      message: "🗑️ Item deleted!",
      severity: "info",
    });
  };

  const formatText = (format) => {
    const textarea = document.querySelector("textarea");
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = generatedContent.substring(start, end);
    let formattedText = "";

    switch (format) {
      case "bold":
        formattedText = `**${selectedText}**`;
        break;
      case "italic":
        formattedText = `*${selectedText}*`;
        break;
      case "bullet":
        formattedText = `\n• ${selectedText}`;
        break;
      case "number":
        formattedText = `\n1. ${selectedText}`;
        break;
      default:
        formattedText = selectedText;
    }

    const newContent =
      generatedContent.substring(0, start) +
      formattedText +
      generatedContent.substring(end);
    setGeneratedContent(newContent);
  };

  const handleFullscreenFormatText = (format) => {
    const editor = document.querySelector("#fullscreen-editor");
    if (!editor) return;

    const selection = window.getSelection();
    if (!selection.rangeCount) return;

    const range = selection.getRangeAt(0);
    const selectedText = range.toString();

    if (!selectedText) return;

    let formattedText = "";
    switch (format) {
      case "bold":
        formattedText = `**${selectedText}**`;
        break;
      case "italic":
        formattedText = `*${selectedText}*`;
        break;
      default:
        formattedText = selectedText;
    }

    const newNode = document.createTextNode(formattedText);
    range.deleteContents();
    range.insertNode(newNode);

    // Update formatted content
    const newFormattedContent = editor.textContent || "";
    setFormattedContent(newFormattedContent);

    // Extract raw content by removing formatting markers
    const rawContent = newFormattedContent
      .replace(/\*\*/g, "")
      .replace(/\*/g, "")
      .replace(
        /✨|🚀|💡|🌟|🔥|🎯|💎|⚡|📊|💼|🏆|⭐|📈|🎨|💰|🏢|🛠️|👤|👥|📱|💪|😟|🎂|🤝/g,
        "",
      )
      .trim();
    setGeneratedContent(rawContent);
  };

  const handleAddEmoji = () => {
    const emojiMap = {
      slogan: ["🚀", "💡", "✨", "🌟", "🔥", "🎯", "💎", "⚡"],
      strategy: ["📊", "🎯", "🚀", "💼", "🏆", "⭐", "📈", "🎨"],
      idea: ["💡", "🚀", "🌟", "💰", "🏢", "🛠️", "🎯", "✨"],
      persona: ["👤", "🎯", "💼", "👥", "📊", "🤝", "💡", "🌟"],
    };
    const toolEmojis = emojiMap[activeTool.id] || ["✨", "💡", "🌟"];
    const randomEmoji =
      toolEmojis[Math.floor(Math.random() * toolEmojis.length)];

    const editor = document.querySelector("#fullscreen-editor");
    if (!editor) return;

    const selection = window.getSelection();
    if (!selection.rangeCount) return;

    const range = selection.getRangeAt(0);
    const newNode = document.createTextNode(`${randomEmoji} `);
    range.insertNode(newNode);

    // Update formatted content
    const newFormattedContent = editor.textContent || "";
    setFormattedContent(newFormattedContent);

    // Extract raw content by removing formatting markers
    const rawContent = newFormattedContent
      .replace(/\*\*/g, "")
      .replace(/\*/g, "")
      .replace(
        /✨|🚀|💡|🌟|🔥|🎯|💎|⚡|📊|💼|🏆|⭐|📈|🎨|💰|🏢|🛠️|👤|👥|📱|💪|😟|🎂|🤝/g,
        "",
      )
      .trim();
    setGeneratedContent(rawContent);
  };

  const filteredSavedContents = savedContents.filter(
    (item) =>
      item.tool.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.content.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  const starredContents = filteredSavedContents.filter((item) =>
    starredItems.has(item.id),
  );
  const allContents = filteredSavedContents;

  const displayedContents = selectedTab === 0 ? allContents : starredContents;

  // Modern Fullscreen Loader Component
  const FullscreenLoader = ({ tool }) => (
    <Fade in={loading && loadingTool === tool.id} timeout={300}>
      <Backdrop
        open={loading && loadingTool === tool.id}
        sx={{
          zIndex: 9999,
          color: "#fff",
          background: tool.gradient,
          display: "flex",
          flexDirection: "column",
          gap: 3,
          backdropFilter: "blur(10px)",
          backgroundColor: alpha(tool.color, 0.95),
        }}
      >
        {/* Floating particles */}
        <Box
          sx={{
            position: "absolute",
            width: "100%",
            height: "100%",
            overflow: "hidden",
          }}
        >
          {[...Array(8)].map((_, i) => (
            <Box
              key={i}
              sx={{
                position: "absolute",
                width: Math.random() * 60 + 20,
                height: Math.random() * 60 + 20,
                background: alpha("#fff", Math.random() * 0.2 + 0.05),
                borderRadius: "50%",
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                animation: `float ${Math.random() * 3 + 2}s ease-in-out infinite`,
                animationDelay: `${Math.random() * 1}s`,
                filter: "blur(2px)",
                "@keyframes float": {
                  "0%, 100%": { transform: "translateY(0) rotate(0deg)" },
                  "50%": { transform: "translateY(-20px) rotate(180deg)" },
                },
              }}
            />
          ))}
        </Box>

        {/* Main Loader Content */}
        <Box
          sx={{
            textAlign: "center",
            zIndex: 1,
            maxWidth: 500,
            mx: "auto",
            px: 2,
          }}
        >
          {/* Animated Icon Container */}
          <Box
            sx={{
              width: 100,
              height: 100,
              borderRadius: "50%",
              background: `radial-gradient(circle at 30% 30%, ${alpha("#fff", 0.2)}, transparent)`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              margin: "0 auto 32px",
              position: "relative",
              boxShadow: `
                0 0 60px ${alpha(tool.color, 0.4)},
                inset 0 0 20px ${alpha("#fff", 0.1)}
              `,
            }}
          >
            {/* Pulsing Ring */}
            <Box
              sx={{
                position: "absolute",
                width: "120%",
                height: "120%",
                borderRadius: "50%",
                border: `2px solid ${alpha("#fff", 0.2)}`,
                animation: "pulseRing 2s ease-out infinite",
                "@keyframes pulseRing": {
                  "0%": {
                    transform: "scale(0.8)",
                    opacity: 0.8,
                  },
                  "100%": {
                    transform: "scale(1.2)",
                    opacity: 0,
                  },
                },
              }}
            />

            {/* Rotating Icon */}
            <Box
              sx={{
                animation: "rotate 3s linear infinite",
                "@keyframes rotate": {
                  "0%": { transform: "rotate(0deg)" },
                  "100%": { transform: "rotate(360deg)" },
                },
              }}
            >
              <Box
                sx={{
                  animation: "counterRotate 3s linear infinite",
                  "@keyframes counterRotate": {
                    "0%": { transform: "rotate(0deg)" },
                    "100%": { transform: "rotate(-360deg)" },
                  },
                }}
              >
                {React.cloneElement(tool.icon, {
                  sx: { fontSize: 48, color: "#fff" },
                })}
              </Box>
            </Box>
          </Box>

          {/* Loading Text */}
          <Box sx={{ mb: 4 }}>
            <Typography
              variant="h3"
              fontWeight="bold"
              sx={{
                mb: 2,
                textShadow: "0 2px 10px rgba(0,0,0,0.2)",
                fontSize: { xs: "2rem", sm: "2.5rem" },
              }}
            >
              Crafting Your {tool.name}
            </Typography>
            <Typography
              variant="h6"
              sx={{
                opacity: 0.9,
                fontWeight: 400,
                mb: 3,
                maxWidth: 400,
                mx: "auto",
              }}
            >
              Our AI is generating unique content tailored for your needs
            </Typography>
          </Box>

          {/* Progress Bar */}
          <Box sx={{ width: "100%", maxWidth: 400, mx: "auto", mb: 4 }}>
            <Box
              sx={{
                width: "100%",
                height: 4,
                backgroundColor: alpha("#fff", 0.2),
                borderRadius: 2,
                overflow: "hidden",
              }}
            >
              <Box
                sx={{
                  width: "75%",
                  height: "100%",
                  background:
                    "linear-gradient(90deg, #fff, rgba(255,255,255,0.5))",
                  borderRadius: 2,
                  animation: "progressShimmer 2s ease-in-out infinite",
                  "@keyframes progressShimmer": {
                    "0%": { transform: "translateX(-100%)" },
                    "100%": { transform: "translateX(400%)" },
                  },
                }}
              />
            </Box>
            <Typography
              variant="caption"
              sx={{
                display: "block",
                textAlign: "center",
                mt: 1,
                opacity: 0.7,
                fontSize: "0.875rem",
              }}
            >
              This usually takes 10-30 seconds
            </Typography>
          </Box>

          {/* Loading Dots */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              gap: 2,
              mt: 4,
            }}
          >
            {[...Array(3)].map((_, i) => (
              <Box
                key={i}
                sx={{
                  width: 12,
                  height: 12,
                  borderRadius: "50%",
                  backgroundColor: "#fff",
                  animation: `bounce 1.4s infinite ${i * 0.2}s`,
                  "@keyframes bounce": {
                    "0%, 80%, 100%": {
                      transform: "scale(0)",
                      opacity: 0.5,
                    },
                    "40%": {
                      transform: "scale(1)",
                      opacity: 1,
                    },
                  },
                }}
              />
            ))}
          </Box>

          {/* Subtle Tips */}
          <Box
            sx={{
              position: "absolute",
              bottom: 60,
              left: 0,
              right: 0,
              textAlign: "center",
              zIndex: 1,
            }}
          >
            <Typography
              variant="caption"
              sx={{
                opacity: 0.6,
                fontSize: "0.75rem",
                letterSpacing: "0.5px",
                textTransform: "uppercase",
              }}
            >
              Powered by Advanced AI • Secure Processing
            </Typography>
          </Box>
        </Box>
      </Backdrop>
    </Fade>
  );

  // Modern Card Loader (for generate button)
  const CardLoader = () => (
    <Box
      sx={{
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: alpha("#fff", 0.92),
        backdropFilter: "blur(4px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 100,
        borderRadius: 3,
        border: `1px solid ${alpha(activeTool.color, 0.2)}`,
      }}
    >
      <Box sx={{ textAlign: "center", px: 3 }}>
        {/* Spinning Gradient Circle */}
        <Box
          sx={{
            width: 60,
            height: 60,
            borderRadius: "50%",
            margin: "0 auto 16px",
            position: "relative",
            background: `conic-gradient(${activeTool.color} 0deg, ${alpha(activeTool.color, 0.3)} 360deg)`,
            animation: "spin 1s linear infinite",
            "@keyframes spin": {
              "0%": { transform: "rotate(0deg)" },
              "100%": { transform: "rotate(360deg)" },
            },
            "&::before": {
              content: '""',
              position: "absolute",
              inset: 4,
              background: "#fff",
              borderRadius: "50%",
            },
          }}
        >
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              zIndex: 1,
            }}
          >
            {React.cloneElement(activeTool.icon, {
              sx: { fontSize: 24, color: activeTool.color },
            })}
          </Box>
        </Box>

        <Typography
          variant="h6"
          color="text.primary"
          fontWeight="bold"
          sx={{ mb: 1 }}
        >
          Generating {activeTool.name}
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            maxWidth: 300,
            mx: "auto",
            lineHeight: 1.4,
          }}
        >
          Please wait while our AI creates amazing content for you...
        </Typography>

        {/* Mini Progress Dots */}
        <Box sx={{ display: "flex", justifyContent: "center", gap: 1, mt: 3 }}>
          {[...Array(5)].map((_, i) => (
            <Box
              key={i}
              sx={{
                width: 6,
                height: 6,
                borderRadius: "50%",
                backgroundColor: activeTool.color,
                opacity: 0.3,
                animation: `pulseMini 1s ease-in-out infinite ${i * 0.1}s`,
                "@keyframes pulseMini": {
                  "0%, 100%": { opacity: 0.3, transform: "scale(1)" },
                  "50%": { opacity: 1, transform: "scale(1.2)" },
                },
              }}
            />
          ))}
        </Box>
      </Box>
    </Box>
  );

  return (
    <Container
      maxWidth="xl"
      sx={{
        py: 4,
        minHeight: "100vh",
        backgroundColor: theme.palette.mode === "light" ? "#f8fafc" : "#0f172a",
        position: "relative",
      }}
    >
      {/* Individual Tool Loaders */}
      {tools.map((tool) => (
        <FullscreenLoader key={tool.id} tool={tool} />
      ))}

      {/* Modern Breadcrumb */}
      <Box sx={{ mb: 4 }}>
        <Paper
          elevation={0}
          sx={{
            p: 2.5,
            borderRadius: 3,
            backgroundColor: "background.paper",
            border: "1px solid",
            borderColor: "divider",
            transition: "all 0.3s ease",
            "&:hover": {
              boxShadow: "0 4px 12px rgba(102, 126, 234, 0.15)",
            },
          }}
        >
          <Breadcrumbs
            separator={
              <NavigateNextIcon fontSize="small" sx={{ color: "#667eea" }} />
            }
            aria-label="breadcrumb"
          >
            {/* First breadcrumb item */}
            <Box
              component={RouterLink}
              to="/docTags"
              onClick={(e) => handleBreadcrumbClick(e, "/docTags")}
              sx={{
                textDecoration: "none",
                display: "flex",
                alignItems: "center",
                color: "#667eea",
                fontWeight: 500,
                fontSize: "0.95rem",
                padding: "6px 12px",
                borderRadius: "6px",
                backgroundColor: "rgba(102, 126, 234, 0.08)",
                whiteSpace: "nowrap",
                flexShrink: 0,
                transition: "all 0.2s ease",
                "&:hover": {
                  backgroundColor: "rgba(102, 126, 234, 0.12)",
                  color: "#764ba2",
                  transform: "translateY(-1px)",
                },
              }}
            >
              <AutoFixHigh sx={{ fontSize: 20, mr: 1 }} />
              MultiTool AI
            </Box>

            {/* Current page */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                padding: "6px 12px",
                borderRadius: "6px",
                backgroundColor: "rgba(102, 126, 234, 0.08)",
              }}
            >
              <AutoAwesome sx={{ fontSize: 20, color: "#667eea", mr: 1 }} />
              <Typography
                sx={{ color: "#1a1a1a", fontWeight: 600, fontSize: "0.95rem" }}
              >
                AI Business Tools
              </Typography>
            </Box>
          </Breadcrumbs>
        </Paper>
      </Box>

      {/* Centered Header */}
      <Box
        sx={{
          textAlign: "center",
          mb: 4,
          position: "relative",
          maxWidth: 600,
          mx: "auto",
        }}
      >
        {/* Decorative Background */}
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: "100%",
            height: "100%",
            background:
              "radial-gradient(circle at center, transparent 30%, rgba(99, 102, 241, 0.03) 70%, transparent 100%)",
            filter: "blur(40px)",
            zIndex: 0,
          }}
        />

        <Box sx={{ position: "relative", zIndex: 1 }}>
          {/* Animated Icon */}
          {/* <Box
            sx={{
              display: "inline-flex",
              p: 2,
              borderRadius: "20%",
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              color: "white",
              mb: 3,
              animation: "floatIcon 3s ease-in-out infinite",
              "@keyframes floatIcon": {
                "0%, 100%": { transform: "translateY(0) rotate(0deg)" },
                "50%": { transform: "translateY(-10px) rotate(10deg)" },
              },
            }}
          >
            <AutoAwesome sx={{ fontSize: 20 }} />
          </Box> */}

          <Typography
            variant="h2"
            component="h1"
            gutterBottom
            sx={{
              fontWeight: "bold",
              background:
                "linear-gradient(135deg, #667eea 0%, #764ba2 50%, #f59e0b 100%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              mb: 2,
              fontSize: { xs: "1.5rem", md: "2.5rem" },
            }}
          >
            AI Business Tools
          </Typography>
          <Typography
            variant="h6"
            sx={{
              color: "text.secondary",
              maxWidth: 600,
              mx: "auto",
              lineHeight: 1.6,
              fontWeight: 100,
            }}
          >
            {/* Generate professional business content, strategies, and ideas with
            cutting-edge AI technology. Perfect for entrepreneurs, marketers,
            and business owners. */}

          Generate a Professional-grade business insights, marketing strategies,
             and growth ideas tailored for entrepreneurs and business leaders
          </Typography>
        </Box>
      </Box>

      <Grid container spacing={3}>
        {/* Left Panel - Tools & Questions */}
        <Grid xs={12} lg={8}>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              borderRadius: 3,
              backgroundColor: "background.paper",
              height: "100%",
              position: "relative",
              border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
              boxShadow: `
                0 4px 6px -1px ${alpha(theme.palette.common.black, 0.05)},
                0 10px 15px -3px ${alpha(theme.palette.common.black, 0.05)}
              `,
              overflow: "hidden",
            }}
          >
            {/* Inline Loader for Generate Button */}
            {loading && loadingTool === activeTool.id && <CardLoader />}

            {/* Tool Selection */}
            <Box sx={{ mb: 4 }}>
              <Typography
                variant="h6"
                fontWeight="bold"
                sx={{ mb: 2, display: "flex", alignItems: "center", gap: 1 }}
              >
                <Palette sx={{ color: activeTool.color }} />
                Select AI Tool
              </Typography>
              <Grid container spacing={2}>
                {tools.map((tool) => (
                  <Grid xs={6} sm={3} key={tool.id}>
                    <Card
                      onClick={() => handleToolClick(tool)}
                      sx={{
                        cursor: "pointer",
                        transition: "all 0.3s ease",
                        border:
                          activeTool.id === tool.id
                            ? `2px solid ${tool.color}`
                            : "1px solid",
                        borderColor:
                          activeTool.id === tool.id ? tool.color : "divider",
                        backgroundColor:
                          activeTool.id === tool.id
                            ? alpha(tool.color, 0.05)
                            : "background.paper",
                        borderRadius: 2,
                        height: "100%",
                        minHeight: 120,
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                        position: "relative",
                        overflow: "hidden",
                        "&::before": {
                          content: '""',
                          position: "absolute",
                          top: 0,
                          left: 0,
                          right: 0,
                          height: "4px",
                          background: tool.gradient,
                          opacity: activeTool.id === tool.id ? 1 : 0,
                          transition: "opacity 0.3s ease",
                        },
                        "&:hover": {
                          transform: "translateY(-4px)",
                          boxShadow: `0 12px 24px ${alpha(tool.color, 0.15)}`,
                          borderColor: tool.color,
                          "&::before": { opacity: 1 },
                        },
                      }}
                    >
                      <CardContent
                        sx={{
                          textAlign: "center",
                          p: 2,
                          "&:last-child": { pb: 2 },
                        }}
                      >
                        <Box
                          sx={{
                            display: "inline-flex",
                            p: 1.5,
                            borderRadius: "12px",
                            background: tool.gradient,
                            color: "white",
                            mb: 1.5,
                            transition: "all 0.3s ease",
                            transform:
                              activeTool.id === tool.id
                                ? "scale(1.1) rotate(5deg)"
                                : "scale(1)",
                          }}
                        >
                          {tool.icon}
                        </Box>
                        <Typography
                          variant="subtitle2"
                          fontWeight="bold"
                          sx={{ mb: 0.5 }}
                        >
                          {tool.name}
                        </Typography>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{
                            display: "block",
                            fontSize: "0.7rem",
                            lineHeight: 1.2,
                          }}
                        >
                          {tool.description}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Box>

            {/* Active Tool Content */}
            <Box
              sx={{
                p: 3,
                borderRadius: 2,
                backgroundColor: alpha(activeTool.color, 0.03),
                border: `1px solid ${alpha(activeTool.color, 0.1)}`,
                mb: 3,
                position: "relative",
                overflow: "hidden",
                "&::before": {
                  content: '""',
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  height: "4px",
                  background: activeTool.gradient,
                },
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                <Box
                  sx={{
                    display: "inline-flex",
                    p: 1.5,
                    borderRadius: "12px",
                    background: activeTool.gradient,
                    color: "white",
                    mr: 2,
                    animation: "pulse 2s ease-in-out infinite",
                    "@keyframes pulse": {
                      "0%, 100%": {
                        boxShadow: `0 0 0 0 ${alpha(activeTool.color, 0.4)}`,
                      },
                      "50%": {
                        boxShadow: `0 0 0 10px ${alpha(activeTool.color, 0)}`,
                      },
                    },
                  }}
                >
                  {activeTool.icon}
                </Box>
                <Box>
                  <Typography
                    variant="h5"
                    fontWeight="bold"
                    color={activeTool.color}
                  >
                    {activeTool.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Answer these questions to generate amazing content
                  </Typography>
                </Box>
              </Box>

              {activeTool.questions.map((question, index) => (
                <Box key={index} sx={{ mb: 3 }}>
                  <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                    <Box
                      sx={{
                        width: 28,
                        height: 28,
                        borderRadius: "8px",
                        background: activeTool.gradient,
                        color: "white",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        mr: 2,
                        fontSize: "0.875rem",
                        fontWeight: "bold",
                        flexShrink: 0,
                        boxShadow: `0 2px 8px ${alpha(activeTool.color, 0.3)}`,
                      }}
                    >
                      {index + 1}
                    </Box>
                    <Typography variant="body1" fontWeight="medium">
                      {question}
                    </Typography>
                  </Box>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    variant="outlined"
                    placeholder={activeTool.placeholder}
                    value={answers[index]}
                    onChange={(e) => handleAnswerChange(index, e.target.value)}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 1,
                        transition: "all 0.2s",
                        "&:hover .MuiOutlinedInput-notchedOutline": {
                          borderColor: activeTool.color,
                          borderWidth: 2,
                        },
                        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                          borderColor: activeTool.color,
                          borderWidth: 2,
                        },
                      },
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <ChatBubbleOutline
                            sx={{
                              fontSize: 18,
                              color: alpha(activeTool.color, 0.6),
                            }}
                          />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Box>
              ))}

              <Box sx={{ display: "flex", gap: 2, mt: 4 }}>
                <Button
                  variant="contained"
                  startIcon={
                    loading && loadingTool === activeTool.id ? (
                      <CircularProgress size={20} color="inherit" />
                    ) : (
                      <AutoAwesome />
                    )
                  }
                  onClick={() => {
                    if (activeTool.id === "slogan") {
                      generateSloganFromAPI();
                    } else if (activeTool.id === "strategy") {
                      generateBrandStrategyFromAPI();
                    } else if (activeTool.id === "idea") {
                      generateBusinessIdeaFromAPI();
                    } else if (activeTool.id === "persona") {
                      generatePersonaFromAPI();
                    }
                  }}
                  size="large"
                  disabled={loading}
                  sx={{
                    flex: 1,
                    background: activeTool.gradient,
                    "&:hover": {
                      background: activeTool.gradient,
                      boxShadow: `0 8px 30px ${alpha(activeTool.color, 0.4)}`,
                      transform: "translateY(-2px)",
                    },
                    py: 1.5,
                    borderRadius: 1,
                    position: "relative",
                    overflow: "hidden",
                    fontWeight: "bold",
                    fontSize: "1rem",
                    transition: "all 0.3s ease",
                    "&::before": {
                      content: '""',
                      position: "absolute",
                      top: 0,
                      left: "-100%",
                      width: "100%",
                      height: "100%",
                      background:
                        "linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)",
                      transition: "left 0.7s ease",
                    },
                    "&:hover::before": {
                      left: "100%",
                    },
                  }}
                >
                  {loading && loadingTool === activeTool.id
                    ? "Generating..."
                    : "Generate Content"}
                </Button>

                <Button
                  variant="outlined"
                  startIcon={<Refresh />}
                  onClick={handleClear}
                  size="large"
                  disabled={loading}
                  sx={{
                    borderRadius: 1,
                    borderColor: alpha(activeTool.color, 0.5),
                    color: activeTool.color,
                    "&:hover": {
                      borderColor: activeTool.color,
                      backgroundColor: alpha(activeTool.color, 0.05),
                      transform: "translateY(-1px)",
                    },
                    transition: "all 0.2s ease",
                  }}
                >
                  Clear
                </Button>
              </Box>
            </Box>
          </Paper>
        </Grid>

        {/* Right Panel - Generated Content & Actions */}
        <Grid xs={12} lg={4}>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              borderRadius: 3,
              backgroundColor: "background.paper",
              height: "100%",
              display: "flex",
              flexDirection: "column",
              border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
              boxShadow: `
                0 4px 6px -1px ${alpha(theme.palette.common.black, 0.05)},
                0 10px 15px -3px ${alpha(theme.palette.common.black, 0.05)}
              `,
            }}
          >
            {/* Header with Actions */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                mb: 3,
              }}
            >
              <Typography variant="h6" fontWeight="bold">
                Generated Content
              </Typography>
              <Box sx={{ display: "flex", gap: 1 }}>
                <ModernTooltip title="Fullscreen">
                  <IconButton
                    size="small"
                    onClick={() => setFullscreenDialogOpen(true)}
                    disabled={!generatedContent}
                  >
                    <Fullscreen fontSize="small" />
                  </IconButton>
                </ModernTooltip>
              </Box>
            </Box>

            {/* Editor Toolbar */}
            {showEditorToolbar && (
              <Paper
                elevation={0}
                sx={{
                  p: 1,
                  mb: 2,
                  borderRadius: 1,
                  backgroundColor: alpha(theme.palette.primary.main, 0.05),
                  border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                  display: "flex",
                  gap: 0.5,
                  flexWrap: "wrap",
                }}
              >
                <ModernTooltip title="Bold">
                  <IconButton size="small" onClick={() => formatText("bold")}>
                    <FormatBold fontSize="small" />
                  </IconButton>
                </ModernTooltip>
                <ModernTooltip title="Italic">
                  <IconButton size="small" onClick={() => formatText("italic")}>
                    <FormatItalic fontSize="small" />
                  </IconButton>
                </ModernTooltip>
                <Divider orientation="vertical" flexItem />
                <ModernTooltip title="Bullet List">
                  <IconButton size="small" onClick={() => formatText("bullet")}>
                    <FormatListBulleted fontSize="small" />
                  </IconButton>
                </ModernTooltip>
                <ModernTooltip title="Numbered List">
                  <IconButton size="small" onClick={() => formatText("number")}>
                    <FormatListNumbered fontSize="small" />
                  </IconButton>
                </ModernTooltip>
                <Divider orientation="vertical" flexItem />
                <ModernTooltip title="Add Link">
                  <IconButton size="small">
                    <Link fontSize="small" />
                  </IconButton>
                </ModernTooltip>
                <ModernTooltip title="Add Image">
                  <IconButton size="small">
                    <Image fontSize="small" />
                  </IconButton>
                </ModernTooltip>
              </Paper>
            )}

            {/* Generated Content Display */}
            <Box
              sx={{ flex: 1, mb: 3, position: "relative", overflow: "auto" }}
            >
              {generatedContent ? (
                <Box
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    backgroundColor: alpha(activeTool.color, 0.03),
                    border: `1px solid ${alpha(activeTool.color, 0.1)}`,
                    minHeight: 400,
                    maxHeight: "calc(100vh - 400px)",
                    overflow: "auto",
                  }}
                >
                  {/* Content Header with Tool Info */}
                  <Box
                    sx={{
                      mb: 3,
                      pb: 2,
                      borderBottom: `2px solid ${alpha(activeTool.color, 0.2)}`,
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1.5,
                        mb: 1,
                      }}
                    >
                      <Box
                        sx={{
                          width: 36,
                          height: 36,
                          borderRadius: "10px",
                          background: activeTool.gradient,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          color: "white",
                        }}
                      >
                        {activeTool.icon}
                      </Box>
                      <Box>
                        <Typography
                          variant="h6"
                          fontWeight="bold"
                          color={activeTool.color}
                        >
                          {activeTool.name} Results
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Generated just now
                        </Typography>
                      </Box>
                    </Box>
                  </Box>

                  {/* Formatted Content */}
                  <Box sx={{ fontFamily: "'Inter', 'Roboto', sans-serif" }}>
                    {renderFormattedContent(formattedContent)}
                  </Box>

                  {/* Content Stats */}
                  <Box
                    sx={{
                      mt: 3,
                      pt: 2,
                      borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Typography variant="caption" color="text.secondary">
                      {formattedContent.split(/\s+/).filter(Boolean).length}{" "}
                      words • {formattedContent.length} characters
                    </Typography>
                    <Chip
                      label="Formatted"
                      size="small"
                      icon={<AutoAwesome fontSize="small" />}
                      sx={{
                        backgroundColor: alpha(activeTool.color, 0.1),
                        color: activeTool.color,
                        fontWeight: 500,
                      }}
                    />
                  </Box>
                </Box>
              ) : (
                <Box
                  sx={{
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    p: 4,
                    textAlign: "center",
                    borderRadius: 2,
                    backgroundColor: alpha(theme.palette.divider, 0.05),
                    border: `2px dashed ${alpha(theme.palette.divider, 0.2)}`,
                    minHeight: 400,
                  }}
                >
                  <AutoAwesome
                    sx={{
                      fontSize: 48,
                      color: alpha(theme.palette.text.secondary, 0.3),
                      mb: 2,
                    }}
                  />
                  <Typography variant="h6" color="text.secondary" gutterBottom>
                    Generated Content
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Your AI-generated content will appear here. Fill in the
                    questions on the left and click "Generate Content" to get
                    started.
                  </Typography>
                </Box>
              )}
            </Box>

            {/* Action Buttons */}
            <Box sx={{ display: "flex", gap: 2, mt: "auto" }}>
              <Tooltip title="Copy to clipboard">
                <Button
                  variant="outlined"
                  startIcon={<ContentCopy />}
                  onClick={handleCopy}
                  disabled={!generatedContent}
                  fullWidth
                  sx={{
                    borderRadius: 1,
                    borderColor: alpha(activeTool.color, 0.3),
                    color: activeTool.color,
                    "&:hover": {
                      borderColor: activeTool.color,
                      backgroundColor: alpha(activeTool.color, 0.05),
                    },
                  }}
                >
                  Copy
                </Button>
              </Tooltip>

              <Tooltip title="Save for later">
                <Button
                  variant="outlined"
                  startIcon={<Save />}
                  onClick={handleSave}
                  disabled={!generatedContent}
                  fullWidth
                  sx={{
                    borderRadius: 1,
                    borderColor: alpha(activeTool.color, 0.3),
                    color: activeTool.color,
                    "&:hover": {
                      borderColor: activeTool.color,
                      backgroundColor: alpha(activeTool.color, 0.05),
                    },
                  }}
                >
                  Save
                </Button>
              </Tooltip>

              <Tooltip title="Download as text file">
                <Button
                  variant="contained"
                  startIcon={<Download />}
                  onClick={handleDownload}
                  disabled={!generatedContent}
                  fullWidth
                  sx={{
                    borderRadius: 1,
                    background: activeTool.gradient,
                    "&:hover": {
                      background: activeTool.gradient,
                      boxShadow: `0 8px 20px ${alpha(activeTool.color, 0.3)}`,
                    },
                  }}
                >
                  Download
                </Button>
              </Tooltip>
            </Box>
          </Paper>
        </Grid>

        {/* Saved Content Section */}
        <Grid xs={12}>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              borderRadius: 3,
              backgroundColor: "background.paper",
              border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
              boxShadow: `
                0 4px 6px -1px ${alpha(theme.palette.common.black, 0.05)},
                0 10px 15px -3px ${alpha(theme.palette.common.black, 0.05)}
              `,
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                mb: 3,
              }}
            >
              <Typography variant="h6" fontWeight="bold">
                Saved Content
              </Typography>
              <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                <TextField
                  size="small"
                  placeholder="Search saved content..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ width: 250 }}
                />
                <Tabs
                  value={selectedTab}
                  onChange={(e, newValue) => setSelectedTab(newValue)}
                  sx={{ minHeight: 36 }}
                >
                  <Tab
                    icon={<Article />}
                    label="All"
                    sx={{ minHeight: 36, fontSize: "0.75rem" }}
                  />
                  <Tab
                    icon={<Star />}
                    label="Starred"
                    sx={{ minHeight: 36, fontSize: "0.75rem" }}
                  />
                </Tabs>
              </Box>
            </Box>

            {displayedContents.length > 0 ? (
              <List>
                {displayedContents.map((item) => (
                  <ListItem
                    key={item.id}
                    sx={{
                      mb: 2,
                      borderRadius: 2,
                      border: `1px solid ${alpha(item.color, 0.1)}`,
                      backgroundColor: alpha(item.color, 0.03),
                      transition: "all 0.2s ease",
                      "&:hover": {
                        backgroundColor: alpha(item.color, 0.05),
                        transform: "translateY(-2px)",
                        boxShadow: `0 4px 12px ${alpha(item.color, 0.1)}`,
                      },
                    }}
                  >
                    <ListItemIcon>
                      <Box
                        sx={{
                          p: 1,
                          borderRadius: "10px",
                          background: `linear-gradient(135deg, ${item.color} 0%, ${alpha(item.color, 0.7)} 100%)`,
                          color: "white",
                        }}
                      >
                        {item.icon}
                      </Box>
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          <Typography variant="subtitle1" fontWeight="bold">
                            {item.tool}
                          </Typography>
                          <Chip
                            label={new Date(item.date).toLocaleDateString()}
                            size="small"
                            sx={{ height: 20, fontSize: "0.65rem" }}
                          />
                        </Box>
                      }
                      secondary={
                        <Typography variant="body2" color="text.secondary">
                          {item.content}
                        </Typography>
                      }
                    />
                    <Box sx={{ display: "flex", gap: 1 }}>
                      <Tooltip
                        title={starredItems.has(item.id) ? "Unstar" : "Star"}
                      >
                        <IconButton
                          size="small"
                          onClick={() => handleStarToggle(item.id)}
                        >
                          {starredItems.has(item.id) ? (
                            <Star sx={{ color: "#fbbf24" }} />
                          ) : (
                            <StarBorder />
                          )}
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton
                          size="small"
                          onClick={() => handleDeleteSaved(item.id)}
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </ListItem>
                ))}
              </List>
            ) : (
              <Box
                sx={{
                  textAlign: "center",
                  py: 8,
                  borderRadius: 2,
                  backgroundColor: alpha(theme.palette.divider, 0.03),
                  border: `2px dashed ${alpha(theme.palette.divider, 0.2)}`,
                }}
              >
                <Save
                  sx={{
                    fontSize: 48,
                    color: alpha(theme.palette.text.secondary, 0.3),
                    mb: 2,
                  }}
                />
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  No saved content yet
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Generate and save content to see it here
                </Typography>
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Modern Fullscreen Dialog */}
      <Dialog
        fullScreen
        open={fullscreenDialogOpen}
        onClose={() => setFullscreenDialogOpen(false)}
        TransitionComponent={Fade}
        PaperProps={{
          sx: {
            background:
              theme.palette.mode === "light"
                ? "linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)"
                : "linear-gradient(135deg, #0f172a 0%, #1e293b 100%)",
          },
        }}
      >
        <Box
          sx={{
            height: "100vh",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          {/* Modern Header with Gradient */}
          <Paper
            elevation={0}
            sx={{
              p: 2.5,
              background: activeTool.gradient,
              color: "white",
              position: "relative",
              overflow: "hidden",
              "&::before": {
                content: '""',
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background:
                  "radial-gradient(circle at 20% 80%, rgba(255,255,255,0.2) 0%, transparent 50%)",
              },
            }}
          >
            <Box
              sx={{
                position: "relative",
                zIndex: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              {/* Left Section */}
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <IconButton
                  onClick={() => setFullscreenDialogOpen(false)}
                  sx={{
                    color: "white",
                    backgroundColor: "rgba(255, 255, 255, 0.15)",
                    backdropFilter: "blur(10px)",
                    "&:hover": {
                      backgroundColor: "rgba(255, 255, 255, 0.25)",
                    },
                  }}
                >
                  <FullscreenExit />
                </IconButton>

                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                  <Box
                    sx={{
                      width: 44,
                      height: 44,
                      borderRadius: "12px",
                      background: "rgba(255, 255, 255, 0.2)",
                      backdropFilter: "blur(10px)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      border: "1px solid rgba(255, 255, 255, 0.3)",
                    }}
                  >
                    {React.cloneElement(activeTool.icon, {
                      sx: { fontSize: 24, color: "white" },
                    })}
                  </Box>
                  <Box>
                    <Typography variant="h6" fontWeight="bold">
                      {activeTool.name} - Fullscreen Editor
                    </Typography>
                    <Typography variant="caption" sx={{ opacity: 0.9 }}>
                      Edit and refine your formatted content
                    </Typography>
                  </Box>
                </Box>
              </Box>

              {/* Right Section - Action Buttons */}
              <Box sx={{ display: "flex", gap: 1 }}>
                <Button
                  variant="contained"
                  startIcon={<ContentCopy />}
                  onClick={handleCopyFormatted}
                  size="small"
                  sx={{
                    backgroundColor: "rgba(255, 255, 255, 0.15)",
                    backdropFilter: "blur(10px)",
                    color: "white",
                    border: "1px solid rgba(255, 255, 255, 0.2)",
                    "&:hover": {
                      backgroundColor: "rgba(255, 255, 255, 0.25)",
                    },
                  }}
                >
                  Copy
                </Button>
                <Button
                  variant="contained"
                  startIcon={<Download />}
                  onClick={handleDownload}
                  size="small"
                  sx={{
                    backgroundColor: "white",
                    color: activeTool.color,
                    "&:hover": {
                      backgroundColor: "rgba(255, 255, 255, 0.95)",
                    },
                  }}
                >
                  Download
                </Button>
              </Box>
            </Box>
          </Paper>

          {/* Main Content Area */}
          <Box
            sx={{
              flex: 1,
              display: "flex",
              overflow: "hidden",
              p: 3,
              gap: 3,
            }}
          >
            {/* Editor Panel - Left */}
            <Paper
              elevation={0}
              sx={{
                flex: 3,
                display: "flex",
                flexDirection: "column",
                borderRadius: 3,
                overflow: "hidden",
                border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                backgroundColor: "background.paper",
                boxShadow:
                  theme.palette.mode === "light"
                    ? "0 4px 24px rgba(102, 126, 234, 0.08)"
                    : "0 4px 24px rgba(0, 0, 0, 0.2)",
              }}
            >
              {/* Editor Header */}
              <Box
                sx={{
                  p: 2,
                  borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                  backgroundColor: alpha(activeTool.color, 0.03),
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <Typography
                    variant="subtitle1"
                    fontWeight="bold"
                    color={activeTool.color}
                  >
                    Formatted Content Editor
                  </Typography>
                  <Chip
                    label="AI Generated"
                    size="small"
                    icon={<AutoAwesome fontSize="small" />}
                    sx={{
                      backgroundColor: alpha(activeTool.color, 0.1),
                      color: activeTool.color,
                      fontWeight: 500,
                    }}
                  />
                </Box>
                <Typography variant="caption" color="text.secondary">
                  {formattedContent.length} characters •{" "}
                  {formattedContent.split(/\s+/).filter(Boolean).length} words
                </Typography>
              </Box>

              {/* Editor Toolbar */}
              <Paper
                elevation={0}
                sx={{
                  p: 1.5,
                  borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                  backgroundColor: alpha(theme.palette.background.default, 0.5),
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                  flexWrap: "wrap",
                }}
              >
                <Tooltip title="Bold">
                  <IconButton
                    size="small"
                    onClick={() => handleFullscreenFormatText("bold")}
                    sx={{
                      borderRadius: 1,
                      "&:hover": {
                        backgroundColor: alpha(activeTool.color, 0.1),
                      },
                    }}
                  >
                    <FormatBold fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Italic">
                  <IconButton
                    size="small"
                    onClick={() => handleFullscreenFormatText("italic")}
                    sx={{
                      borderRadius: 1,
                      "&:hover": {
                        backgroundColor: alpha(activeTool.color, 0.1),
                      },
                    }}
                  >
                    <FormatItalic fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Divider
                  orientation="vertical"
                  flexItem
                  sx={{ height: 24, mx: 0.5 }}
                />

                <Tooltip title="Add Emoji">
                  <Button
                    size="small"
                    startIcon={<AutoAwesome />}
                    onClick={handleAddEmoji}
                    sx={{
                      color: activeTool.color,
                      "&:hover": {
                        backgroundColor: alpha(activeTool.color, 0.1),
                      },
                    }}
                  >
                    Emoji
                  </Button>
                </Tooltip>

                <Box sx={{ flex: 1 }} />
              </Paper>

              {/* Editor Content - Formatted Display */}
              <Box
                sx={{
                  flex: 1,
                  overflow: "auto",
                  position: "relative",
                  p: 3,
                  backgroundColor: alpha(activeTool.color, 0.02),
                }}
              >
                <Box
                  id="fullscreen-editor"
                  contentEditable
                  suppressContentEditableWarning
                  onInput={(e) => {
                    const newContent = e.currentTarget.textContent || "";
                    setFormattedContent(newContent);

                    // Extract raw content by removing formatting markers
                    const rawContent = newContent
                      .replace(/\*\*/g, "")
                      .replace(/\*/g, "")
                      .replace(
                        /✨|🚀|💡|🌟|🔥|🎯|💎|⚡|📊|💼|🏆|⭐|📈|🎨|💰|🏢|🛠️|👤|👥|📱|💪|😟|🎂|🤝/g,
                        "",
                      )
                      .trim();
                    setGeneratedContent(rawContent);
                  }}
                  style={{
                    minHeight: "calc(100vh - 250px)",
                    outline: "none",
                    fontFamily: "'Inter', 'Roboto', sans-serif",
                    fontSize: "1rem",
                    lineHeight: 1.7,
                    color: theme.palette.text.primary,
                    whiteSpace: "pre-wrap",
                  }}
                >
                  {renderFormattedContent(formattedContent)}
                </Box>

                {/* Placeholder when empty */}
                {!formattedContent && (
                  <Box
                    sx={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: theme.palette.text.disabled,
                      pointerEvents: "none",
                    }}
                  >
                    <Typography variant="body1" color="text.disabled">
                      Start editing your formatted content here...
                    </Typography>
                  </Box>
                )}
              </Box>

              {/* Editor Footer */}
              <Box
                sx={{
                  p: 2,
                  borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  backgroundColor: alpha(theme.palette.background.default, 0.5),
                }}
              >
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Last saved: Just now • Auto-save enabled
                  </Typography>
                  <Box sx={{ display: "flex", gap: 1, mt: 0.5 }}>
                    <Chip
                      label="Formatted View"
                      size="small"
                      sx={{
                        backgroundColor: alpha(activeTool.color, 0.1),
                        color: activeTool.color,
                        fontSize: "0.7rem",
                        height: 20,
                      }}
                    />
                    <Chip
                      label="Emojis Enabled"
                      size="small"
                      sx={{
                        backgroundColor: alpha(activeTool.color, 0.1),
                        color: activeTool.color,
                        fontSize: "0.7rem",
                        height: 20,
                      }}
                    />
                  </Box>
                </Box>
                <Box display="flex" gap={1}>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => setFullscreenDialogOpen(false)}
                    startIcon={<Close />}
                  >
                    Close
                  </Button>
                  <Button
                    variant="contained"
                    size="small"
                    onClick={handleSave}
                    startIcon={<Save />}
                    sx={{
                      background: activeTool.gradient,
                      "&:hover": {
                        background: activeTool.gradient,
                        boxShadow: `0 4px 20px ${alpha(activeTool.color, 0.4)}`,
                      },
                    }}
                  >
                    Save Changes
                  </Button>
                </Box>
              </Box>
            </Paper>
          </Box>
        </Box>
      </Dialog>

      {/* Save Dialog */}
      <Dialog open={saveDialogOpen} onClose={() => setSaveDialogOpen(false)}>
        <DialogTitle>Save Generated Content</DialogTitle>
        <DialogContent>
          <Typography>
            Your content has been saved to your collection.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSaveDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};
export default AIBusinessTools;
